import React from "react";
import Drawer from "../../Drawer/Drawer";
import { toast } from "react-toastify";

// Returns categories that are candidates for "must pick exactly one as mandatory".
// Normal case: whatever backend flags is_optional === 0 (if more than one).
// Fallback: Grooming/Boarding plan sends BOTH as is_optional: 1, so when there
// are zero mandatory-flagged categories, detect the groom/board pair by name.
// This never fires for Fetch/Care since they already have one is_optional:0 cat.
const getMandatoryCandidates = (plan) => {
  const planCats = plan?.plan_category || [];
  const mandatoryFlagged = planCats.filter((cat) => cat.is_optional === 0);
  if (mandatoryFlagged.length > 1) return mandatoryFlagged;

  if (mandatoryFlagged.length === 0) {
    const primaryPairCandidates = planCats.filter((cat) => {
      const name = cat.service_category_name?.toLowerCase() || "";
      return (
        name.includes("groom") ||
        name.includes("board") ||
        name.includes("resort")
      );
    });
    if (primaryPairCandidates.length > 1) return primaryPairCandidates;
  }

  return mandatoryFlagged;
};

const needsPrimaryChoice = (plan) => getMandatoryCandidates(plan).length > 1;

const getOptionalCategoryIds = (plan, mainId) => {
  const planCats = plan?.plan_category || [];
  const mandatoryCandidates = getMandatoryCandidates(plan);

  if (needsPrimaryChoice(plan)) {
    return planCats
      .filter((cat) => {
        const isPairMember = mandatoryCandidates.some(
          (c) => c.service_category_id === cat.service_category_id,
        );
        // String() guards against "5" (from <select> value) !== 5 (from data)
        return isPairMember
          ? String(cat.service_category_id) !== String(mainId)
          : cat.is_optional === 1;
      })
      .map((cat) => cat.service_category_id);
  }

  return planCats
    .filter((cat) => cat.is_optional === 1)
    .map((cat) => cat.service_category_id);
};

const PlanChangeDrawer = ({
  isOpen,
  onClose,
  plans,
  loadingPlans,
  profileData,
  onUpgradeDowngrade,
  onConfirm,
  selectedPlan,
  selectedOptionalCategories,
  setSelectedOptionalCategories,
  selectedPrimaryCategory,
  setSelectedPrimaryCategory,
  showUpgradeConfirm,
  showDowngradeConfirm,
  setShowUpgradeConfirm,
  setShowDowngradeConfirm,
  getActivePlanInfo,
  shouldShowDowngrade,
}) => {

   const isPlanExpired = () => {
     const subscription = profileData?.subscriptionPlan?.[0];
     if (!subscription) return false;

     if (subscription.is_expired === true) return true;

     const endDate = subscription.end_date;
     if (!endDate) return false;
     const expiry = new Date(endDate);
     const today = new Date();
     expiry.setHours(0, 0, 0, 0);
     today.setHours(0, 0, 0, 0);
     return expiry <= today;
   };
  // ─── Keep selectedOptionalCategories in sync with the primary dropdown ───
  const [initialized, setInitialized] = React.useState(false);

  React.useEffect(() => {
    if (!showUpgradeConfirm && !showDowngradeConfirm) {
      setInitialized(false);
    }
  }, [showUpgradeConfirm, showDowngradeConfirm]);

  React.useEffect(() => {
    if (
      (showUpgradeConfirm || showDowngradeConfirm) &&
      selectedPlan &&
      !initialized
    ) {
      let defaultPrimary = selectedPrimaryCategory;
      if (needsPrimaryChoice(selectedPlan) && !defaultPrimary) {
        const candidates = getMandatoryCandidates(selectedPlan);
        defaultPrimary = candidates[0]?.service_category_id;
        setSelectedPrimaryCategory(defaultPrimary);
      }
      // Optional categories start UNCHECKED for upgrade/downgrade — the user
      // must opt in. For "Update Services" on the active plan, usePlanChange
      // already prefilled selections from the vendor's current data, so we
      // leave them alone here.
      // Select ALL optional categories by default
      if (!selectedPlan._toggleOnly) {
        const allOptionalIds = getOptionalCategoryIds(
          selectedPlan,
          defaultPrimary,
        );
        setSelectedOptionalCategories(allOptionalIds);
      }
      setInitialized(true);
    }
  }, [showUpgradeConfirm, showDowngradeConfirm, selectedPlan]);

  // New effect: recalculate optional selections when primary changes, but never clear everything
  React.useEffect(() => {
    if (!selectedPlan || !(showUpgradeConfirm || showDowngradeConfirm)) return;

    const planCats = selectedPlan.plan_category || [];
    const mandatoryCandidates = getMandatoryCandidates(selectedPlan);
    const showPrimaryDropdown = mandatoryCandidates.length > 1;

    // Compute which categories are currently considered "optional"
    const currentlyOptionalIds = planCats
      .filter((cat) => {
        const isPairMember = mandatoryCandidates.some(
          (c) => c.service_category_id === cat.service_category_id,
        );
        if (showPrimaryDropdown && isPairMember) {
          // The non-selected member of the pair becomes optional
          return (
            String(cat.service_category_id) !== String(selectedPrimaryCategory)
          );
        }
        return cat.is_optional === 1;
      })
      .map((cat) => cat.service_category_id);

    // Keep only those previously selected optional IDs that are still optional
    setSelectedOptionalCategories((prev) =>
      prev.filter((id) => currentlyOptionalIds.includes(id)),
    );
  }, [
    selectedPrimaryCategory,
    selectedPlan,
    showUpgradeConfirm,
    showDowngradeConfirm,
  ]);




  // ─── RENDER PLANS (same as Onlineservice) ───
  const renderUpgradeDowngradeContent = () => {
    const { planName: currentPlanName } = getActivePlanInfo();

    const freePlan = plans.find(
      (p) =>
        p.plan_price_per_month === "0" ||
        p.plan_price_per_month === 0 ||
        p.plan_name?.toLowerCase().includes("free"),
    );

    const paidPlans = [...plans]
      .filter((p) => p._id !== freePlan?._id)
      .sort((a, b) => (a.plan_type || 999) - (b.plan_type || 999));

    const isCurrentPlan = (plan) =>
      plan.plan_name?.toLowerCase() === currentPlanName?.toLowerCase();

    const isFreeCurrent = freePlan && isCurrentPlan(freePlan);

    if (loadingPlans) {
      return (
        <div className="p-4 text-center text-gray-500">Loading plans...</div>
      );
    }

    return (
      <div className="w-full p-2 space-y-4">
        {/* Free Plan Banner */}
        {freePlan && (
          <div
            className={`flex items-center justify-between rounded-xl px-5 py-3 border transition-all duration-200 ${
              isFreeCurrent
                ? "border-blue-400 bg-gradient-to-r from-blue-50/70 to-white ring-2 ring-blue-200 shadow-sm"
                : "border-gray-200 bg-gradient-to-r from-gray-50/50 to-white hover:border-gray-300"
            }`}
          >
            <div className="flex flex-col md:flex-row md:items-center gap-4 flex-1">
              <div className="space-y-0.5">
                <div className="flex items-center gap-2">
                  <h3 className="text-lg font-medium text-gray-900">
                    {freePlan.plan_name}
                  </h3>
                  {isFreeCurrent && (
                    <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-blue-100 text-blue-700">
                      Active Plan
                    </span>
                  )}
                </div>
                <p className="text-sm text-gray-500 max-w-xl">
                  {freePlan.plan_title || "Basic access with limited features."}
                </p>
              </div>
              <div className="flex items-center gap-4 border-l border-gray-100 pl-4 h-full">
                <div>
                  <span className="text-lg font-bold text-gray-900">₹0</span>
                  <span className="text-sm text-gray-500 ml-0.5">/month</span>
                </div>
                <div className="text-sm text-gray-600 flex items-center gap-1">
                  <svg
                    width="12"
                    height="12"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    className="text-gray-400"
                  >
                    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                    <circle cx="9" cy="7" r="4" />
                  </svg>
                  <span>Up to {freePlan.user_limit || 1} user</span>
                </div>
              </div>
              <div className="flex gap-2 flex-wrap md:ml-auto max-w-md">
                {(freePlan.plan_category || []).flatMap((cat, catIdx) =>
                  (cat.service_type || []).map((stype, stIdx) => {
                    const isOptional = cat.is_optional === 1;
                    const uniqueKey = `free-feat-${catIdx}-${stIdx}`;
                    return (
                      <div
                        key={uniqueKey}
                        className="flex items-center gap-2 text-xs px-2 py-1 rounded bg-orange-50 text-orange-800 border border-orange-100/50 font-medium"
                      >
                        <span>{stype.service_type_name}</span>
                        {isOptional && (
                          <span className="text-[9px] px-1 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-100">
                            optional
                          </span>
                        )}
                      </div>
                    );
                  }),
                )}
              </div>
            </div>
            <div className="ml-4 flex-shrink-0">
              {isFreeCurrent ? (
                <button
                  disabled
                  className="px-4 py-1.5 text-xs font-medium rounded-lg border border-gray-200 bg-gray-50 text-gray-400 cursor-not-allowed"
                >
                  Active
                </button>
              ) : (
                <button
                  onClick={() => onUpgradeDowngrade(freePlan)}
                  className="px-4 py-1.5 text-sm font-medium rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 transition"
                >
                  Switch to free
                </button>
              )}
            </div>
          </div>
        )}

        <div className="pt-1 border-t border-gray-100">
          <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">
            Premium Packages
          </p>
        </div>

        <div className="grid grid-cols-4 gap-4 items-stretch">
          {paidPlans.map((plan, planIdx) => {
            const isCurrent = isCurrentPlan(plan);
            const isDowngrade = shouldShowDowngrade(plan.plan_type);
            const cardBg = isCurrent
              ? "border-blue-400 ring-2 ring-blue-300 shadow-md bg-white"
              : "border-gray-200 hover:border-gray-300 bg-white";

            return (
              <div
                key={plan._id || `paid-col-${planIdx}`}
                className={`rounded-xl border flex flex-col overflow-hidden transition-all duration-200 relative ${cardBg}`}
              >
                {isCurrent && (
                  <div className="absolute -top-1 -right-1 z-10">
                    <span className="inline-block px-4 py-1.5 text-xs font-medium rounded-bl-xl rounded-tr-xl bg-blue-600 text-white shadow-md">
                      ⚡ Current Plan
                    </span>
                  </div>
                )}
                <div
                  className={`p-4 ${isCurrent ? "bg-blue-50/90" : "bg-gray-50/80"} border-b border-gray-100`}
                >
                  <h3
                    className={`text-lg tracking-wide ${isCurrent ? "text-blue-600 font-normal" : "text-gray-800"}`}
                  >
                    {plan.plan_name}
                  </h3>
                  <p className="text-sm text-gray-500 mt-1 min-h-[2.5rem] line-clamp-2 leading-relaxed">
                    {plan.plan_title ||
                      "Access advanced services, tools, and premium features."}
                  </p>
                  <div className="mt-2">
                    <span className="text-2xl font-bold text-gray-900">
                      ₹{plan.plan_price_per_month}
                    </span>
                    <span className="text-sm font-normal text-gray-500 ml-0.5">
                      /month
                    </span>
                  </div>
                </div>
                <div className="p-4 flex-1 flex flex-col bg-white">
                  <div className="flex items-center gap-1.5 text-sm text-gray-600 mb-3 pb-2 border-b border-gray-50">
                    <svg
                      width="13"
                      height="13"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      className="text-gray-400"
                    >
                      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                      <circle cx="9" cy="7" r="4" />
                    </svg>
                    <span>
                      Up to{" "}
                      {plan.user_limit === "unlimited" || !plan.user_limit
                        ? "unlimited"
                        : plan.user_limit}{" "}
                      user{plan.user_limit !== 1 && "s"}
                    </span>
                  </div>
                  <div className="flex-1 min-h-[130px]">
                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">
                      Services Included
                    </p>
                    <div className="space-y-2">
                      {(plan.plan_category || []).flatMap((cat, catIdx) =>
                        (cat.service_type || []).map((stype, stIdx) => {
                          const isOptional = cat.is_optional === 1;
                          const uniqueKey = `paid-feat-${plan._id}-${catIdx}-${stIdx}`;
                          return (
                            <div
                              key={uniqueKey}
                              className="flex items-center justify-between"
                            >
                              <div className="flex items-center gap-1.5">
                                <svg
                                  width="12"
                                  height="12"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  stroke="#185FA5"
                                  strokeWidth="2.5"
                                >
                                  <polyline points="20 6 9 17 4 12" />
                                </svg>
                                <span className="text-xs text-gray-700">
                                  {stype.service_type_name}
                                </span>
                              </div>
                              {isOptional && (
                                <span className="text-[9px] px-1 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-100">
                                  optional
                                </span>
                              )}
                            </div>
                          );
                        }),
                      )}
                    </div>
                  </div>
                  <div className="mt-auto pt-3">
                    {isCurrent ? (
                      <button
                        onClick={() => onUpgradeDowngrade(plan, true)}
                        className="w-full border border-blue-300 text-blue-600 rounded-xl py-2 text-xs font-medium hover:bg-blue-50 transition"
                      >
                        {isPlanExpired() ? "Renew Plan" : "Update Services"}
                      </button>
                    ) : isDowngrade ? (
                      <button
                        onClick={() => onUpgradeDowngrade(plan)}
                        className="w-full bg-gray-900 text-white rounded-xl py-2 text-xs font-medium hover:bg-black transition flex items-center justify-center gap-1"
                      >
                        <svg
                          width="12"
                          height="12"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                        />
                        Switch to {plan.plan_name}
                      </button>
                    ) : (
                      <button
                        onClick={() => onUpgradeDowngrade(plan)}
                        className="w-full bg-gray-900 text-white rounded-xl py-2 text-xs font-medium hover:bg-black transition flex items-center justify-center gap-1"
                      >
                        <svg
                          width="12"
                          height="12"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                        />
                        Switch to {plan.plan_name}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // ─── CONFIRMATION MODAL (same as Onlineservice) ───
  const renderConfirmationModals = () => {
    if (!selectedPlan) return null;

     const isCurrentPlanExpired = isPlanExpired();
    const isUpdate = showUpgradeConfirm && selectedPlan._isUpdate;
    const isUpgrade =
      showUpgradeConfirm &&
      !selectedPlan._toggleOnly &&
      !selectedPlan._isUpdate;
    const isDowngrade =
      showDowngradeConfirm &&
      !selectedPlan._toggleOnly &&
      !selectedPlan._isUpdate;

    // Toggle single service (not used in Sidebar but kept for completeness)
    if (selectedPlan._toggleOnly) {
      return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg max-w-md mx-4 w-full">
            <h3 className="text-lg font-semibold text-gray-800 mb-3">
              {selectedPlan.newStatus === 1 ? "Enable" : "Disable"}{" "}
              {selectedPlan.serviceName}?
            </h3>
            <p className="text-gray-600 mb-4 text-sm">
              {selectedPlan.newStatus === 1
                ? "Customers will be able to book this service."
                : "This service will be hidden from customers."}
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => {
                  setShowUpgradeConfirm(false);
                  setShowDowngradeConfirm(false);
                }}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 text-sm"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  // In Sidebar we don't have updateVendorCategory, so just close
                  setShowUpgradeConfirm(false);
                  setShowDowngradeConfirm(false);
                  toast.info("Service toggle not available here.");
                }}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      );
    }

    // Plan change modal
    if (isUpgrade || isDowngrade || isUpdate) {
      const planCats = selectedPlan.plan_category || [];
      const mandatoryCandidates = getMandatoryCandidates(selectedPlan);
      const showPrimaryDropdown = mandatoryCandidates.length > 1;

      const getCategoryStatus = (cat) => {
        const isPairMember = mandatoryCandidates.some(
          (c) => c.service_category_id === cat.service_category_id,
        );
        if (showPrimaryDropdown && isPairMember) {
          return selectedPrimaryCategory &&
            String(cat.service_category_id) === String(selectedPrimaryCategory)
            ? "mandatory"
            : "optional";
        }
        return cat.is_optional === 0 ? "mandatory" : "optional";
      };

      const mandatoryCats = planCats.filter(
        (cat) => getCategoryStatus(cat) === "mandatory",
      );
      const optionalCats = planCats.filter(
        (cat) => getCategoryStatus(cat) === "optional",
      );

      const isConfirmDisabled = showPrimaryDropdown && !selectedPrimaryCategory;

      return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg max-w-md mx-4 w-full max-h-[90vh] overflow-y-auto">
            <h3
              className={`text-lg font-semibold mb-4 ${
                isUpdate
                  ? "text-blue-600"
                  : isUpgrade
                    ? "text-blue-600"
                    : "text-blue-600"
              }`}
            >
              {isUpdate
                ? isCurrentPlanExpired
                  ? "Renew Plan"
                  : "Update Services for"
                : isUpgrade
                  ? "Switch to"
                  : " Switch to"}{" "}
              {selectedPlan.plan_name}
            </h3>
            <p className="text-gray-700 mb-4 text-sm">
              {isUpdate
                ? "Add or remove optional services on your current plan. Mandatory services are always included."
                : isUpgrade
                  ? "Select which optional services you want to enable. Mandatory services are always included."
                  : " Select which optional services you want to keep. Unselected optional services will be removed."}
            </p>

            {/* Primary dropdown if both grooming and boarding exist */}
            {/* Primary dropdown only when the plan has >1 mandatory category */}
            {showPrimaryDropdown && (
              <div className="mb-4">
                <label className="font-semibold text-gray-800 text-sm block mb-1">
                  Primary Service (choose one as mandatory)
                </label>
                <select
                  value={selectedPrimaryCategory || ""}
                  onChange={(e) => setSelectedPrimaryCategory(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {mandatoryCandidates.map((cat) => (
                    <option
                      key={cat.service_category_id}
                      value={cat.service_category_id}
                    >
                      {cat.service_category_name}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  The selected service will be mandatory; the other will become
                  optional.
                </p>
              </div>
            )}

            {/* Mandatory services */}
            <div className="mb-4">
              <h4 className="font-semibold text-gray-800 text-sm">
                Mandatory Services
              </h4>
              <ul className="text-sm text-gray-600 space-y-1 mt-1">
                {mandatoryCats.map((cat) => (
                  <li
                    key={cat.service_category_id}
                    className="flex items-center gap-2"
                  >
                    <span className="text-green-600">✔</span>{" "}
                    {cat.service_category_name}
                  </li>
                ))}
              </ul>
            </div>

            {/* Optional services with checkboxes */}
            <div className="mb-4">
              <div className="flex justify-between items-center mb-2">
                <h4 className="font-semibold text-gray-800 text-sm">
                  Optional Services
                </h4>
                {optionalCats.length > 0 && (
                  <button
                    type="button"
                    onClick={() => {
                      const allOptionalIds = optionalCats.map(
                        (cat) => cat.service_category_id,
                      );
                      const allSelected = allOptionalIds.every((id) =>
                        selectedOptionalCategories.includes(id),
                      );
                      setSelectedOptionalCategories(
                        allSelected ? [] : allOptionalIds,
                      );
                    }}
                    className="text-xs text-blue-600 hover:underline"
                  >
                    {optionalCats.every((cat) =>
                      selectedOptionalCategories.includes(
                        cat.service_category_id,
                      ),
                    )
                      ? "Unselect All"
                      : "Select All"}
                  </button>
                )}
              </div>
              <div className="space-y-2 mt-2">
                {optionalCats.map((cat) => {
                  const catId = cat.service_category_id;
                  const isChecked = selectedOptionalCategories.includes(catId);
                  return (
                    <label key={catId} className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={isChecked}
                        onChange={() => {
                          setSelectedOptionalCategories((prev) =>
                            isChecked
                              ? prev.filter((id) => id !== catId)
                              : [...prev, catId],
                          );
                        }}
                        className="w-4 h-4 text-blue-500 border-gray-300 rounded focus:ring-blue-500"
                      />
                      <span className="text-sm text-gray-700">
                        {cat.service_category_name}
                      </span>
                    </label>
                  );
                })}
                {optionalCats.length === 0 && (
                  <p className="text-sm text-gray-500">
                    No optional services available.
                  </p>
                )}
              </div>
            </div>

            {/* Downgrade warning */}
            {/* Downgrade warning */}
            {isDowngrade && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
                <p className="text-sm text-red-700">
                  <strong>Warning:</strong> Your current services will be
                  overwritten by the ones you select.
                </p>
              </div>
            )}

            <div className="flex gap-3 justify-end">
              <button
                onClick={() => {
                  setShowUpgradeConfirm(false);
                  setShowDowngradeConfirm(false);
                }}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 text-sm"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  /*
                  let reason;
                  if (selectedPlan._isUpdate) {
                    reason = "update";
                  } else if (showUpgradeConfirm) {
                    reason = "upgrade";
                  } else {
                    reason = "downgrade";
                  }

                  onConfirm(
                    selectedPlan,
                    reason,
                    selectedPrimaryCategory,
                    selectedOptionalCategories,
                  );
                  */


                  // Show the requested toast message
                  toast.info(
                    "Plan upgrades and renewals are currently unavailable.",
                  );

                  // Close the modals
                  setShowUpgradeConfirm(false);
                  setShowDowngradeConfirm(false);
                }}
                disabled={isConfirmDisabled}
                className={`px-4 py-2 rounded-lg text-white text-sm ${
                  selectedPlan._isUpdate
                    ? "bg-blue-600 hover:bg-blue-700"
                    : showUpgradeConfirm
                      ? "bg-green-600 hover:bg-green-700"
                      : "bg-red-600 hover:bg-red-700"
                } ${isConfirmDisabled ? "opacity-50 cursor-not-allowed" : ""}`}
              >
                {selectedPlan._isUpdate
                  ? "Save Changes"
                  : showUpgradeConfirm
                    ? "Confirm Upgrade"
                    : "Confirm Downgrade"}
              </button>
            </div>
          </div>
        </div>
      );
    }

    return null;
  };

  return (
    <>
      {/* Main Drawer */}
      <Drawer
        visible={isOpen}
        onClose={onClose}
        title="Select Your Subscription Plan"
        className="!w-[98vw] sm:!w-[95vw] md:!w-[92vw] lg:!w-[90vw]"
        style={{ maxWidth: "1600px" }}
        hideFooter
      >
        {renderUpgradeDowngradeContent()}
      </Drawer>

      {/* Confirmation Modals */}
      {renderConfirmationModals()}
    </>
  );
};;;

export default PlanChangeDrawer;
